import { useState, useEffect, useRef } from 'react';
import API from '../utils/API';
import './awards.css';

const CAT_META = {
  bridge_builder: { icon: 'bi-diagram-2-fill', color: '#5b8dde' },
  lobby_legend: { icon: 'bi-star-fill', color: '#c4900a' },
  rising_star: { icon: 'bi-stars', color: '#8b5cf6' },
  wonky_wizard: { icon: 'bi-lightbulb-fill', color: '#10a37f' },
  the_translator: { icon: 'bi-chat-quote-fill', color: '#e4645a' },
  honest_abe: { icon: 'bi-shield-check', color: '#1a2744' },
  intrepid_innovator: { icon: 'bi-lightning-fill', color: '#e0803a' },
  hidden_hero: { icon: 'bi-person-hearts', color: '#c2658a' },
};

export default function Awards() {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('nominate');

  // Nomination
  const [form, setForm] = useState({
    category: '', nominee_name: '', nominee_email: '',
    nominee_years_experience: '', reason: '',
    nominee_title: '', nominee_employer: '', nominee_profile_id: '',
  });
  const [myNominations, setMyNominations] = useState([]);
  const [nomError, setNomError] = useState(null);
  const [nomSuccess, setNomSuccess] = useState(null);
  const [nomSubmitting, setNomSubmitting] = useState(false);
  const [showMyNoms, setShowMyNoms] = useState(false);

  // Hero sparkles
  const [sparkles, setSparkles] = useState([]);
  useEffect(() => {
    const ids = { schedule: null, clear: null };
    const fire = () => {
      const count = 12 + Math.floor(Math.random() * 8);
      const raw = Array.from({ length: count }, (_, id) => ({
        id,
        x: 5 + Math.random() * 90,
        y: 10 + Math.random() * 80,
        size: 5 + Math.random() * 7,
      }));
      const dir = Math.floor(Math.random() * 4);
      const sorted = [...raw].sort((a, b) => {
        if (dir === 0) return a.x - b.x;
        if (dir === 1) return b.x - a.x;
        if (dir === 2) return (a.x + a.y) - (b.x + b.y);
        return (a.x - a.y) - (b.x - b.y);
      });
      setSparkles(sorted.map((s, i) => ({ ...s, delay: i * 55 })));
      ids.clear = setTimeout(() => setSparkles([]), 2200);
      ids.schedule = setTimeout(fire, 8000 + Math.random() * 8000);
    };
    ids.schedule = setTimeout(fire, 8000 + Math.random() * 8000);
    return () => { clearTimeout(ids.schedule); clearTimeout(ids.clear); };
  }, []);

  // Nominee autocomplete
  const [nomineeSearch, setNomineeSearch] = useState('');
  const [nomineeResults, setNomineeResults] = useState([]);
  const [nomineeSearching, setNomineeSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const dropdownRef = useRef(null);

  // Countdown to nominations open
  const [countdown, setCountdown] = useState(null);
  useEffect(() => {
    if (!settings?.nominations_enabled || settings?.nominations_open || !settings?.nominations_start) {
      setCountdown(null);
      return;
    }
    const target = new Date(settings.nominations_start);
    const compute = () => {
      const diff = target - new Date();
      if (diff <= 0) { setCountdown(null); return false; }
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
      return true;
    };
    if (!compute()) return;
    const id = setInterval(() => { if (!compute()) clearInterval(id); }, 1000);
    return () => clearInterval(id);
  }, [settings]);

  // Voting
  const [ballot, setBallot] = useState({});
  const [voting, setVoting] = useState({});

  useEffect(() => {
    Promise.all([API.getAwardSettings(), API.getAwardCategories()])
      .then(([sRes, cRes]) => {
        const s = sRes.data;
        setSettings(s);
        setCategories(cRes.data);
        if (!s.nominations_open && s.voting_open) setView('vote');
      })
      .catch(err => console.error('Awards load error:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!settings) return;
    if (settings.nominations_open) {
      API.getMyNominations().then(r => setMyNominations(r.data)).catch(() => {});
    }
    if (settings.voting_open) {
      API.getVotingBallot().then(r => setBallot(r.data)).catch(() => {});
    }
  }, [settings]);

  const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const selectedCat = categories.find(c => c.key === form.category);
  const showYears = selectedCat?.minYears != null || selectedCat?.maxYears != null;
  const catLabelMap = Object.fromEntries(categories.map(c => [c.key, c.label]));

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced nominee search
  useEffect(() => {
    if (nomineeSearch.length < 2 || selectedProfile) {
      setNomineeResults([]);
      setShowDropdown(false);
      return;
    }
    const t = setTimeout(() => {
      setNomineeSearching(true);
      API.searchNominees(nomineeSearch)
        .then(r => { setNomineeResults(r.data); setShowDropdown(r.data.length > 0); })
        .catch(() => {})
        .finally(() => setNomineeSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [nomineeSearch, selectedProfile]);

  const handleNomineeSearchChange = (e) => {
    const val = e.target.value;
    setNomineeSearch(val);
    setField('nominee_name', val);
    if (selectedProfile) {
      setSelectedProfile(null);
      setField('nominee_email', '');
      setField('nominee_title', '');
      setField('nominee_employer', '');
      setField('nominee_profile_id', '');
    }
    if (val.length < 2) setShowDropdown(false);
  };

  const selectNominee = (p) => {
    setSelectedProfile(p);
    setNomineeSearch(p.name);
    setShowDropdown(false);
    setNomineeResults([]);
    setField('nominee_name', p.name);
    setField('nominee_email', p.email);
    setField('nominee_title', p.title);
    setField('nominee_employer', p.employer);
    setField('nominee_profile_id', p.profile_id);
  };

  const clearNominee = () => {
    setSelectedProfile(null);
    setNomineeSearch('');
    setNomineeResults([]);
    setShowDropdown(false);
    setField('nominee_name', '');
    setField('nominee_email', '');
    setField('nominee_title', '');
    setField('nominee_employer', '');
    setField('nominee_profile_id', '');
  };

  const handleNominate = async (e) => {
    e.preventDefault();
    if (!form.category) { setNomError('Please select a category.'); return; }
    setNomError(null);
    setNomSubmitting(true);
    try {
      await API.submitNomination({
        category: form.category,
        nominee_profile_id: form.nominee_profile_id || undefined,
        nominee_name: form.nominee_name,
        nominee_email: form.nominee_email,
        nominee_years_experience: showYears ? form.nominee_years_experience : undefined,
        nominee_title: form.nominee_title || undefined,
        nominee_employer: form.nominee_employer || undefined,
        reason: form.reason,
      });
      setNomSuccess(
        `Nomination submitted! Your ${selectedCat?.label ?? ''} nomination is pending the nominee's acceptance — they'll receive an email to accept or decline.`
      );
      setForm({ category: '', nominee_name: '', nominee_email: '', nominee_years_experience: '', reason: '', nominee_title: '', nominee_employer: '', nominee_profile_id: '' });
      setNomineeSearch('');
      setSelectedProfile(null);
      API.getMyNominations().then(r => setMyNominations(r.data)).catch(() => {});
    } catch (err) {
      setNomError(err.response?.data?.msg || 'Failed to submit nomination. Please try again.');
    } finally {
      setNomSubmitting(false);
    }
  };

  const handleVote = async (category, nominee_email) => {
    setVoting(v => ({ ...v, [category]: true }));
    try {
      await API.castVote({ category, nominee_email });
      setBallot(b => ({ ...b, [category]: { ...b[category], my_vote: nominee_email } }));
    } catch (err) {
      console.error('Vote error:', err.response?.data?.msg || err.message);
    } finally {
      setVoting(v => ({ ...v, [category]: false }));
    }
  };

  if (loading) return <div className="awards-page"><div className="awards-loading">Loading…</div></div>;

  const neitherOpen = !settings?.nominations_open && !settings?.voting_open;
  const bothOpen = settings?.nominations_open && settings?.voting_open;
  const ballotEntries = Object.entries(ballot);

  return (
    <div className="awards-page">
      <div className="awards-hero">
        <div className="awards-hero-glow" />
        {sparkles.map(s => (
          <div
            key={s.id}
            className="hero-sparkle"
            style={{ left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, animationDelay: `${s.delay}ms` }}
          />
        ))}
        <i className="bi bi-trophy-fill awards-hero-icon" />
        <div className="awards-hero-text">
          <h1 className="awards-hero-title">Peer Awards</h1>
          <p className="awards-hero-subtitle">Recognizing excellence in California's legislative and advocacy community.</p>
        </div>
      </div>

      {countdown && (
        <div className="awards-countdown">
          <p className="awards-countdown-label">
            <i className="bi bi-clock me-1" />Nominations open in
          </p>
          <div className="awards-countdown-units">
            <div className="countdown-unit">
              <span className="countdown-value">{String(countdown.days).padStart(2, '0')}</span>
              <span className="countdown-unit-label">days</span>
            </div>
            <span className="countdown-sep">:</span>
            <div className="countdown-unit">
              <span className="countdown-value">{String(countdown.hours).padStart(2, '0')}</span>
              <span className="countdown-unit-label">hrs</span>
            </div>
            <span className="countdown-sep">:</span>
            <div className="countdown-unit">
              <span className="countdown-value">{String(countdown.minutes).padStart(2, '0')}</span>
              <span className="countdown-unit-label">min</span>
            </div>
            <span className="countdown-sep">:</span>
            <div className="countdown-unit">
              <span className="countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
              <span className="countdown-unit-label">sec</span>
            </div>
          </div>
        </div>
      )}

      {neitherOpen && !countdown && (
        <div className="awards-closed">
          <i className="bi bi-clock awards-closed-icon" />
          <p>Nominations and voting are not currently open. Check back soon.</p>
        </div>
      )}

      {!neitherOpen && (
        <>
          {bothOpen && (
            <div className="awards-tabs">
              <button
                className={`awards-tab ${view === 'nominate' ? 'active' : ''}`}
                onClick={() => setView('nominate')}
              >
                <i className="bi bi-pencil-square me-1" />Nominate
              </button>
              <button
                className={`awards-tab ${view === 'vote' ? 'active' : ''}`}
                onClick={() => setView('vote')}
              >
                <i className="bi bi-check2-square me-1" />Vote
              </button>
            </div>
          )}

          {/* ── Nomination form ── */}
          {view === 'nominate' && settings?.nominations_open && (
            <div className="awards-section">
              {!bothOpen && (
                <h2 className="awards-section-title">
                  <i className="bi bi-pencil-square me-2" />Nominations
                </h2>
              )}

              {nomSuccess ? (
                <div className="awards-success">
                  <span><i className="bi bi-check-circle-fill me-2" />{nomSuccess}</span>
                  <button className="awards-success-again" onClick={() => setNomSuccess(null)}>
                    Nominate someone else
                  </button>
                </div>
              ) : (
                <form className="nom-form" onSubmit={handleNominate}>
                  <div className="nom-field">
                    <label className="nom-label">Category</label>
                    <div className="cat-grid">
                      {categories.map(c => {
                        const meta = CAT_META[c.key] || {};
                        const isSelected = form.category === c.key;
                        return (
                          <button
                            type="button"
                            key={c.key}
                            className={`cat-card ${isSelected ? ' selected' : ''}`}
                            onClick={() => setField('category', c.key)}
                          >
                            <i
                              className={`bi ${meta.icon || 'bi-award'} cat-card-icon`}
                              style={{ color: isSelected ? '#d4a520' : meta.color }}
                            />
                            <span className="cat-card-label">{c.label}</span>
                            {isSelected && <i className="bi bi-check-circle-fill cat-card-check" />}
                          </button>
                        );
                      })}
                    </div>
                    {selectedCat && (
                      <div className="cat-selected-detail">
                        <p className="nom-cat-desc">{selectedCat.description}</p>
                        {selectedCat.minYears != null && (
                          <p className="nom-cat-note">
                            <i className="bi bi-info-circle me-1" />
                            Nominee must have {selectedCat.minYears}+ years of experience.
                          </p>
                        )}
                        {selectedCat?.maxYears != null && (
                          <p className="nom-cat-note">
                            <i className="bi bi-info-circle me-1" />
                            Nominee must have {selectedCat.maxYears} or fewer years of experience.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="nom-field">
                    <label className="nom-label">Nominee</label>
                    <div className="nom-autocomplete" ref={dropdownRef}>
                      <div className="nom-autocomplete-wrap">
                        <input
                          className="nom-input"
                          type="text"
                          placeholder="Search by name…"
                          value={nomineeSearch}
                          onChange={handleNomineeSearchChange}
                          onFocus={() => nomineeResults.length > 0 && setShowDropdown(true)}
                          autoComplete="off"
                          required
                        />
                        {nomineeSearching && <span className="nom-autocomplete-spinner" />}
                        {selectedProfile && (
                          <button type="button" className="nom-autocomplete-clear" onClick={clearNominee} title="Clear">
                            <i className="bi bi-x-lg" />
                          </button>
                        )}
                      </div>
                      {selectedProfile && (
                        <p className="nom-profile-linked">
                          <i className="bi bi-person-check-fill me-1" />Profile matched — details pre-filled below
                        </p>
                      )}
                      {showDropdown && nomineeResults.length > 0 && (
                        <div className="nom-autocomplete-dropdown">
                          {nomineeResults.map(p => (
                            <button
                              type="button"
                              key={p.profile_id}
                              className="nom-autocomplete-item"
                              onMouseDown={() => selectNominee(p)}
                            >
                              <span className="nom-autocomplete-name">{p.name}</span>
                              {(p.title || p.employer) && (
                                <span className="nom-autocomplete-meta">
                                  {[p.title, p.employer].filter(Boolean).join(' @ ')}
                                </span>
                              )}
                              {p.email && <span className="nom-autocomplete-email">{p.email}</span>}
                            </button>
                          ))}
                          <div className="nom-autocomplete-hint">Not listed? Fill in details below.</div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="nom-grid">
                    <div className="nom-field">
                      <label className="nom-label">Email</label>
                      <input
                        className="nom-input"
                        type="email"
                        placeholder="email@example.com"
                        value={form.nominee_email}
                        onChange={e => setField('nominee_email', e.target.value)}
                        required
                      />
                    </div>
                    <div className="nom-field">
                      <label className="nom-label">
                        Title <span className="nom-label-opt">optional</span>
                      </label>
                      <input
                        className="nom-input"
                        type="text"
                        placeholder="e.g. Policy Director"
                        value={form.nominee_title}
                        onChange={e => setField('nominee_title', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="nom-field">
                    <label className="nom-label">
                      Employer <span className="nom-label-opt">optional</span>
                    </label>
                    <input
                      className="nom-input"
                      type="text"
                      placeholder="e.g. California Chamber of Commerce"
                      value={form.nominee_employer}
                      onChange={e => setField('nominee_employer', e.target.value)}
                    />
                  </div>

                  {showYears && (
                    <div className="nom-field">
                      <label className="nom-label">Years of Experience</label>
                      <input
                        className="nom-input nom-input-sm"
                        type="number"
                        min="0"
                        max="60"
                        placeholder="e.g. 12"
                        value={form.nominee_years_experience}
                        onChange={e => setField('nominee_years_experience', e.target.value)}
                        required
                      />
                    </div>
                  )}

                  <div className="nom-field">
                    <label className="nom-label">
                      Why do they deserve this recognition?
                      <span className="nom-char-count">{form.reason.length} / 50 min</span>
                    </label>
                    <textarea
                      className="nom-textarea"
                      rows={4}
                      placeholder="Describe why this person deserves this award…"
                      value={form.reason}
                      onChange={e => setField('reason', e.target.value)}
                      required
                      minLength={50}
                    />
                  </div>

                  {nomError && (
                    <div className="nom-error">
                      <i className="bi bi-exclamation-circle me-1" />{nomError}
                    </div>
                  )}

                  <div className="nom-footer">
                    <button
                      className="nom-submit"
                      type="submit"
                      disabled={nomSubmitting || form.reason.length < 50}
                    >
                      {nomSubmitting ? 'Submitting…' : 'Submit Nomination'}
                    </button>
                  </div>
                </form>
              )}

              {myNominations.length > 0 && (
                <div className="my-noms">
                  <button
                    className="my-noms-toggle"
                    onClick={() => setShowMyNoms(s => !s)}
                  >
                    <i className={`bi bi-chevron-${showMyNoms ? 'up' : 'down'} me-1`} />
                    My Nominations ({myNominations.length})
                  </button>
                  {showMyNoms && (
                    <div className="my-noms-list">
                      {myNominations.map(n => (
                        <div key={n.nomination_id} className="my-nom-row">
                          <div>
                            <span className="my-nom-name">{n.nominee_name}</span>
                            <span className="my-nom-cat">{catLabelMap[n.category] || n.category}</span>
                          </div>
                          <span className={`my-nom-status my-nom-${n.status}`}>{n.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Voting ballot ── */}
          {view === 'vote' && settings?.voting_open && (
            <div className="awards-section">
              {!bothOpen && (
                <h2 className="awards-section-title">
                  <i className="bi bi-check2-square me-2" />Vote
                </h2>
              )}
              <p className="awards-vote-note">
                Cast one vote per category. You can change your vote any time while voting is open.
              </p>
              {ballotEntries.length === 0 ? (
                <div className="awards-closed">
                  <i className="bi bi-inbox awards-closed-icon" />
                  <p>No nominees on the ballot yet. Check back once nominations have been accepted.</p>
                </div>
              ) : (
                <div className="ballot-grid">
                  {ballotEntries.map(([cat, data]) => (
                    <div key={cat} className="ballot-cat">
                      <div className="ballot-cat-header">
                        <i
                          className={`bi ${CAT_META[cat]?.icon || 'bi-award'} ballot-cat-icon`}
                          style={{ color: CAT_META[cat]?.color }}
                        />
                        <span className="ballot-cat-name">{data.label}</span>
                      </div>
                      {data.nominees.map(n => {
                        const isVoted = data.my_vote === n.nominee_email;
                        return (
                          <button
                            key={n.nominee_email}
                            className={`ballot-nominee${isVoted ? ' voted' : ''}`}
                            onClick={() => !voting[cat] && handleVote(cat, n.nominee_email)}
                            disabled={!!voting[cat]}
                          >
                            <div className="ballot-nom-avatar">{n.nominee_name.charAt(0)}</div>
                            <div className="ballot-nom-info">
                              <span className="ballot-nom-name">{n.nominee_name}</span>
                              {(n.nominee_title || n.nominee_employer) && (
                                <span className="ballot-nom-meta">
                                  {[n.nominee_title, n.nominee_employer].filter(Boolean).join(' · ')}
                                </span>
                              )}
                              {isVoted && <span className="ballot-voted-label">Your vote</span>}
                            </div>
                            <i className={`bi ${isVoted ? 'bi-check-circle-fill' : 'bi-circle'} ballot-icon`} />
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
